import { ProTable } from "@ant-design/pro-components";
import { Table } from "antd";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./index.module.less";

/**
 * CustomTable - 自适应高度表格组件
 *
 * 整合 ProTable 与 antd Table，通过 tableType 参数切换。
 * 支持自动计算 scrollY 填满容器（fill 模式）或手动指定（manual 模式）。
 *
 * @example
 * // ProTable, 自动高度
 * <CustomTable
 *   columns={columns}
 *   request={request}
 *   heightOffset={160}
 * />
 *
 * @example
 * // antd Table, 自动高度
 * <CustomTable
 *   tableType="antd-table"
 *   dataSource={data}
 *   columns={columns}
 *   heightOffset={104}
 * />
 *
 * @example
 * // 手动高度
 * <CustomTable
 *   heightMode="manual"
 *   scroll={{ y: 400 }}
 *   columns={columns}
 *   dataSource={data}
 * />
 *
 * heightOffset 常见参考值:
 *   FilterPageLayout + Card（有 toolbar + 分页）: ~160
 *   FilterPageLayout + Card（无分页）:        ~96
 *   ProTable 无 toolbar 有分页:               ~104 (header 40 + pagination 64)
 *   antd Table 有分页:                        ~104 (header 40 + pagination 64)
 *   antd Table 无分页:                         ~40 (仅 header)
 *   Modal / Drawer 内表格:                    根据 chrome 高度调整
 */

export interface CustomTableProps<T extends Record<string, any> = any> {
  /** 表格类型，默认 pro-table */
  tableType?: "pro-table" | "antd-table";
  /** 高度模式: fill=撑满父容器自动计算scrollY, manual=手动指定，默认 fill */
  heightMode?: "fill" | "manual";
  /** 从容器高度中减去的偏移量（toolbar + header + pagination + 间隙） */
  heightOffset?: number;
  /** scrollY 最小值，默认 100 */
  minScrollY?: number;
  /** 容器 className */
  className?: string;
  /** 容器 style */
  style?: React.CSSProperties;
  /** scrollY 变化回调 */
  onScrollYChange?: (scrollY: number) => void;
}

function CustomTable<T extends Record<string, any> = any>(
  props: CustomTableProps<T> & Record<string, any>,
) {
  const {
    tableType = "pro-table",
    heightMode = "fill",
    heightOffset = 0,
    minScrollY = 100,
    className,
    style,
    onScrollYChange,
    scroll: externalScroll,
    ...restProps
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const lastScrollYRef = useRef<number>(minScrollY);
  const [scrollY, setScrollY] = useState<number>(minScrollY);

  // 自动计算 scrollY（fill 模式）
  useLayoutEffect(() => {
    if (heightMode !== "fill") return;

    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const h = Math.max(container.clientHeight - heightOffset, minScrollY);
      if (h !== lastScrollYRef.current) {
        lastScrollYRef.current = h;
        setScrollY(h);
        onScrollYChange?.(h);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [heightMode, heightOffset, minScrollY, onScrollYChange]);

  // 合并 scroll 属性：fill 模式下用计算值覆盖 y，manual 模式下透传
  const mergedScroll = useMemo(() => {
    if (heightMode === "manual") return externalScroll || {};
    return { ...(externalScroll || {}), y: scrollY };
  }, [externalScroll, heightMode, scrollY]);

  // 容器样式
  const containerStyle: React.CSSProperties = useMemo(() => {
    const base: React.CSSProperties = {};
    if (heightMode === "fill") {
      base.height = "100%";
      base.overflow = "hidden";
      (base as any)["--ct-scroll-y"] = `${scrollY}px`;
    }
    return { ...base, ...style };
  }, [heightMode, style, scrollY]);

  const wrapperClassName = useMemo(() => {
    const classes = [className];
    if (heightMode === "fill") classes.push(styles.fillBody);
    return classes.filter(Boolean).join(" ");
  }, [className, heightMode]);

  return (
    <div ref={containerRef} className={wrapperClassName} style={containerStyle}>
      {tableType === "antd-table" ? (
        <Table {...restProps} scroll={mergedScroll} />
      ) : (
        <ProTable {...restProps} scroll={mergedScroll} />
      )}
    </div>
  );
}

export default CustomTable;
